import { Controller } from "@hotwired/stimulus";
import { KeyedTimers } from "../utils/keyed_timers";
import { NUMBER_BOUNDS, type NumberValueConstraints } from "../utils/number_bounds";
import { NumberValueReader } from "../utils/number_value";
import { prefersReducedMotion } from "../utils/reduced_motion";
import { sharedRegistry } from "../utils/shared_registry";
import { TransientHooks } from "../utils/transient_hooks";

/** The hook a connection may find written by an earlier, now-gone one. */
const TRANSIENT = new TransientHooks({ attributes: ["data-highlight"] });

/** A hook's owner and the timer operations belonging to that owner's entry copy. */
interface HighlightOwner {
  readonly owner: HighlightController;
  readonly pending: () => boolean;
  readonly cancel: () => void;
}

/**
 * The connection whose removal timer currently owns an element's `data-highlight`
 * hook. Shared by every instance, so a row moved from one watched container into
 * another still has exactly one timer deciding when its emphasis ends.
 *
 * Only the keys are weak: an entry keeps its controller — and through it that
 * controller's element — reachable for as long as the row is in the DOM. So an entry
 * is dropped as soon as the timer that owns it fires, is replaced when another
 * highlight takes the hook over, and `connect()` drops the claims the connection
 * before it left behind.
 */
const hookOwners = sharedRegistry(
  "stimeo-ui.highlight.registry.v1",
  () => new WeakMap<Element, HighlightOwner>(),
);

/**
 * Headless "highlight on insert" behavior: briefly flags a freshly inserted element
 * with `data-highlight` so CSS can flash / fade it in, then removes the flag after
 * `duration` ms (no dedicated APG pattern; a purely visual emphasis that honors the
 * WCAG "animation from interactions" practice via `prefers-reduced-motion`).
 *
 * Markup contract (identifier: `stimeo--highlight`):
 *   <!-- self-highlight: put it on the inserted row itself -->
 *   <li data-controller="stimeo--highlight">New item</li>
 *
 *   <!-- container mode: watch for added children and highlight each -->
 *   <ul data-controller="stimeo--highlight" data-stimeo--highlight-observe-value="true"></ul>
 *
 * In the default (self) mode the controller element is highlighted once on
 * `connect()`. With `observe`, a `MutationObserver` instead highlights every element
 * child added to the container (e.g. Turbo Stream appends). Each highlight sets
 * `data-highlight="true"`, dispatches `start`, and after `duration` removes it and
 * dispatches `end` — both carry `detail.element`.
 *
 * `start` and `end` dispatch `{ element }`.
 *
 * @remarks
 * Behavior only — it ships no color or animation, just the hook (pair with CSS, and
 * with `stimeo--announcer` / `stimeo--flash` for non-visual notice). Under
 * `prefers-reduced-motion: reduce` the emphasis is suppressed entirely (the element
 * simply appears), so no hook or event is emitted. A hook never outlives the connection
 * that set it: the observer and pending timers are torn down on `disconnect()` (Turbo
 * navigation included), and
 * `connect()` clears any hook that arrived with the DOM — a page Turbo restores from its
 * cache, an in-page move — because the timer that would have removed it is gone.
 * An element carries at most one emphasis at a time: highlighting it again — a reorder
 * inside one container, or a move into another watched one — takes the hook over and
 * releases the timer that held it, so `duration` is measured from the latest highlight
 * and `end` fires once per emphasis.
 *
 * `observe` follows a runtime change (a Turbo morph, an author script): turned on, the
 * element starts watching its children and drops any hook a child carries that no
 * running emphasis owns; turned off, it stops watching. Neither direction is an
 * insertion, so the element does not highlight itself, and an emphasis already running
 * — the element's own or a child's — ends on the deadline it started with. `duration`
 * is read as each emphasis starts: a change applies from the next one.
 */
export class HighlightController extends Controller<HTMLElement> {
  /** Numeric read boundaries share one reader for this controller instance. */
  readonly #numbers = new NumberValueReader();

  static override values = {
    duration: { type: Number, default: 1500 },
    observe: { type: Boolean, default: false },
  };

  static valueConstraints = {
    duration: NUMBER_BOUNDS.timer,
  } satisfies NumberValueConstraints<typeof HighlightController.values>;
  static events = ["start", "end"] as const;

  declare durationValue: number;
  declare observeValue: boolean;

  /**
   * The removal timer this connection has outstanding for each element. Which
   * connection owns an element's hook is answered by the shared owner registry above.
   */
  readonly #removal = new KeyedTimers<Element>();
  #observer: MutationObserver | null = null;
  /** Whether this connection watches its children: the mode it last built. */
  #watching = false;
  /**
   * Gates the mode callback to the connected window. Stimulus delivers it ahead of
   * `connect()`, which builds the mode itself.
   */
  #connected = false;

  override connect(): void {
    // A hook that arrived with the DOM was written by an earlier connection, whose
    // removal timer is gone; this connection owns no timer for it either, so nothing
    // would ever take it off. Drop it before anything else — in self mode
    // `#highlight()` re-adds it with a fresh timer below, except under reduced
    // motion, where the element must carry no hook at all.
    this.#clearArrivedHook(this.element);
    this.#connected = true;
    if (this.observeValue) {
      this.#watchChildren();
      return;
    }
    this.#highlight(this.element);
  }

  override disconnect(): void {
    this.#connected = false;
    this.#stopWatching();
    this.#removal.clearAll();
  }

  /**
   * Follows the mode at runtime, comparing it with the mode this connection built
   * rather than trusting that a callback means a change: two spellings of the same
   * Boolean deliver one too.
   */
  observeValueChanged(): void {
    if (!this.#connected || this.observeValue === this.#watching) return;
    if (this.#watching) this.#stopWatching();
    else this.#watchChildren();
  }

  /**
   * Starts watching for added children. A container highlights its children, never
   * itself, so a hook a child already carries is dropped first unless a running
   * emphasis owns it — that emphasis still has a timer to end it.
   */
  #watchChildren(): void {
    this.#watching = true;
    for (const child of this.element.children) {
      const owner = hookOwners.get(child);
      if (owner === undefined || !owner.pending()) this.#clearArrivedHook(child);
    }
    if (typeof MutationObserver !== "undefined") {
      this.#observer = new MutationObserver((mutations) => this.#onMutations(mutations));
      this.#observer.observe(this.element, { childList: true });
    }
  }

  /** Stops watching for added children; emphases already running keep their timers. */
  #stopWatching(): void {
    this.#watching = false;
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** Drops a hook that arrived with the DOM, along with this connection's claim on it. */
  #clearArrivedHook(el: Element): void {
    // Another connection may still hold a live timer for this element, and that timer
    // has to stay reachable so the next highlight can release it. Only our own claim,
    // whose timer went down with the previous disconnect, is dropped here.
    if (hookOwners.get(el)?.owner === this) hookOwners.delete(el);
    TRANSIENT.reset(el);
  }

  /** Highlights every element child added by a childList mutation. */
  #onMutations(mutations: MutationRecord[]): void {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) this.#highlight(node);
      }
    }
  }

  /**
   * Flags `el` with `data-highlight` and schedules its removal (unless reduced-motion).
   *
   * @stimeoRuntimeOnly `duration` is the length of the one emphasis this call arms.
   */
  #highlight(el: HTMLElement): void {
    // Reduced motion: suppress the emphasis so the element just appears — no hook,
    // no timer, no events, nothing to transition.
    if (prefersReducedMotion()) return;

    // A second highlight of the same element — a re-insert, which is the shape a
    // reorder takes — owns the hook from here on. Release the first timer, or it
    // ends the new emphasis early and then reports an `end` for a hook already gone.
    this.#releasePending(el);
    el.setAttribute("data-highlight", "true");
    this.dispatch("start", { target: el, detail: { element: el } });
    this.#removal.set(
      el,
      () => {
        hookOwners.delete(el);
        el.removeAttribute("data-highlight");
        this.dispatch("end", { target: el, detail: { element: el } });
      },
      this.#safeDuration,
    );
    hookOwners.set(el, {
      owner: this,
      pending: () => this.#removal.has(el),
      cancel: () => this.#cancelPending(el),
    });
  }

  /**
   * Releases whichever removal timer holds `el`'s hook. The row may have been
   * highlighted inside a different watched container before it moved here, and that
   * container's timer is reachable only through the shared owner registry.
   */
  #releasePending(el: HTMLElement): void {
    const owner = hookOwners.get(el);
    if (owner !== undefined && owner.owner !== this) owner.cancel();
  }

  /** Releases `el`'s pending removal timer, if this connection has one. */
  #cancelPending(el: HTMLElement): void {
    this.#removal.clear(el);
  }
  /** Current `duration` declaration resolved against its numeric contract. */
  get #safeDuration(): number {
    return this.#numbers.read(
      this,
      "duration",
      this.durationValue,
      HighlightController.values.duration.default,
      HighlightController.valueConstraints.duration,
    );
  }
}
